import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useMutation } from 'convex/react'
import { useCallback } from 'react'

import { useOnlineStatus } from '@/hooks/use-online-status'
import { optimisticTaskStatus } from '@/lib/nodes-optimistic'
import type { TaskStatus } from '@/store/offline.store'
import { useOfflineStore } from '@/store/offline.store'

/**
 * Returns a setTaskStatus mutation with an optimistic update. `opts` is kept for
 * callers; the update now covers every cached read.
 *
 * When offline the change is queued and replayed on reconnect; the cached-read
 * projections keep the UI consistent in the meantime.
 */
export function useSetTaskStatus(_opts?: { goalId?: Id<'nodes'>; parentId?: Id<'nodes'> | null }) {
  const online = useOnlineStatus()
  const enqueue = useOfflineStore((s) => s.enqueue)
  // Patches every cached read that shows the task (Today, goal tree, inbox, habits…).
  const mutate = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(optimisticTaskStatus)

  return useCallback(
    (args: { taskId: Id<'nodes'>; status: TaskStatus }) => {
      if (online) return mutate(args)
      enqueue({
        kind: 'setTaskStatus',
        args: { taskId: args.taskId as string, status: args.status },
      })
      return Promise.resolve()
    },
    [online, mutate, enqueue],
  )
}
